import React, { useId } from 'react';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { ClinicalTemplate, TemplateField, TemplateSection } from '../../../constants/clinicalTemplates';
import { fieldKey, fieldShown } from './templateUtils';

interface Props {
  template: ClinicalTemplate;
  section: TemplateSection;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  /** Assessment type, for fields shown only for some types */
  assessmentType?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}

const inputClass = (readOnly: boolean, invalid: boolean) =>
  `w-full px-3 py-2 border rounded-md text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${invalid ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'opacity-60 cursor-not-allowed bg-gray-100' : 'bg-white'}`;

/**
 * Phase 20B: one template section (title + typed fields). Checkbox values are stored as the
 * strings 'true' / 'false' in assessments, like the hand-built forms.
 */
const TemplateSectionFields: React.FC<Props> = ({ template, section, values, onChange, errors = {}, readOnly = false, assessmentType, collapsed = false, onToggle }) => {
  const baseId = useId();
  const fields = section.fields.filter(f => fieldShown(f, template.kind === 'assessment' ? assessmentType : undefined));
  if (!fields.length) return null;

  const renderField = (field: TemplateField, index: number) => {
    const key = fieldKey(template, section, field);
    const id = `${baseId}-${index}`;
    const value = values[key];
    const error = errors[key];
    const label = (
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1 text-left">
        {field.name}{field.required && <span className="text-red-600" aria-hidden="true"> *</span>}
      </label>
    );
    let control: React.ReactNode;
    switch (field.type) {
      case 'textarea':
        control = (
          <textarea id={id} rows={3} value={String(value ?? '')} disabled={readOnly} placeholder={field.placeholder}
            aria-invalid={Boolean(error)} onChange={e => onChange(key, e.target.value)} className={inputClass(readOnly, Boolean(error))} />
        );
        break;
      case 'select':
        control = (
          <select id={id} value={String(value ?? '')} disabled={readOnly} aria-invalid={Boolean(error)}
            onChange={e => onChange(key, e.target.value)} className={inputClass(readOnly, Boolean(error))}>
            <option value="">Select...</option>
            {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
        break;
      case 'multiselect': {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        control = (
          <fieldset id={id} aria-invalid={Boolean(error)} className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            <legend className="sr-only">{field.name}</legend>
            {(field.options || []).map(o => (
              <label key={o} className="flex items-center gap-2 text-sm text-gray-800">
                <input type="checkbox" disabled={readOnly} checked={selected.includes(o)}
                  onChange={e => onChange(key, e.target.checked ? [...selected, o] : selected.filter(x => x !== o))} />
                {o}
              </label>
            ))}
          </fieldset>
        );
        break;
      }
      case 'checkbox':
        control = (
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input id={id} type="checkbox" disabled={readOnly} checked={value === true || value === 'true'}
              onChange={e => onChange(key, template.kind === 'assessment' ? String(e.target.checked) : e.target.checked)} />
            {field.name}
          </label>
        );
        break;
      case 'date':
      case 'number':
        control = (
          <input id={id} type={field.type} value={String(value ?? '')} disabled={readOnly} aria-invalid={Boolean(error)}
            onChange={e => onChange(key, e.target.value)} className={inputClass(readOnly, Boolean(error))} />
        );
        break;
      default:
        control = (
          <input id={id} type="text" value={String(value ?? '')} disabled={readOnly} placeholder={field.placeholder} aria-invalid={Boolean(error)}
            onChange={e => onChange(key, e.target.value)} className={inputClass(readOnly, Boolean(error))} />
        );
    }
    const wide = field.type === 'textarea' || field.type === 'multiselect';
    return (
      <div key={key} className={wide ? 'md:col-span-2' : ''}>
        {field.type !== 'checkbox' && label}
        {control}
        {error && <p className="text-red-600 text-sm mt-1 flex items-center" role="alert"><AlertCircle size={14} className="mr-1" aria-hidden="true" />{error}</p>}
      </div>
    );
  };

  return (
    <section className="bg-white rounded-lg border border-gray-200" aria-label={section.name}>
      <button type="button" className="w-full p-4 flex justify-between items-center text-left" onClick={onToggle} aria-expanded={!collapsed}>
        <span className="text-md font-medium text-gray-900">{section.name}</span>
        {collapsed ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronUp size={18} aria-hidden="true" />}
      </button>
      {!collapsed && <div className="p-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">{fields.map(renderField)}</div>}
    </section>
  );
};

export default TemplateSectionFields;
